import { ChangeEvent, FC, useEffect, useState } from "react";
import { FormGroup, Label, Input, FormFeedback, InputGroup, InputGroupText } from "reactstrap";

interface DollarInputProps {
  label?: string;
  name: string;
  value: string;
  setValue: (name: string, value: string) => void;
  setError?: (name: string, error: boolean) => void;
  placeholder?: string;
  validation?: (value: string) => boolean;
  errorMessage?: string;
  error?: boolean;
  required?: boolean;
  comma?: boolean;
  limit?: number;
  disabled?: boolean;
  passCol?: string;
  design?: "flex" | "default";
  tabIndex?: number;
  className?: string;
  noMargin?: boolean;
  style?: any
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const DollarInput: FC<DollarInputProps> = ({
  label,
  name,
  value,
  setValue,
  setError,
  placeholder = "",
  validation = () => true,
  errorMessage = "Invalid input",
  error = false,
  required = false,
  comma = true,
  disabled,
  limit = 0,
  passCol,
  design = "default",
  tabIndex,
  className,
  noMargin = false,
  style,
  onBlur
}) => {
  const [formattedValue, setFormattedValue] = useState(value);
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatValue = (val: string) => {
    let number = val.replace(/[^0-9.]/g, "");

    if (comma) {
      const parts = number.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return parts.join(".");
    } else {
      return number;
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const rawNumericValue = rawValue.replace(/[^0-9.]/g, "");

    if (limit && rawNumericValue.length > limit) {
      return;
    }

    const newValue = formatValue(rawValue);
    setFormattedValue(newValue);
    setValue(name, newValue);

    if (setError) {
      setError(name, false);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!validation(formattedValue) && setError) {
      setError(name, true);
    } else {
      // Logic to save data on blur
      // console.log("Data saved:", formattedValue);
      // You can replace this with your actual save logic
    }
    onBlur?.(e);
  };

  useEffect(() => {
    setFormattedValue(formatValue(value || ""));
  }, [value, comma]);

  const flexClass = design === "flex" && windowWidth > 768 ? "d-flex align-items-center" : "";
  const labelWidthClass = design === "flex" && windowWidth > 768 ? "col-3" : "";
  const passColClass = windowWidth > 768 ? passCol : "";
  const formGroupClass = noMargin ? "" : "mb-3";

  return (
    <FormGroup className={`${flexClass} ${formGroupClass} ${className}`}>
      {label && (
        <Label for={name} className={`${design === "flex" ? "mr-5 " : ""}${labelWidthClass}`}>
          {label}
          {required && <span className="text-danger ml-2">*</span>}
        </Label>
      )}

      <div className={passColClass}>
        {error && <FormFeedback className="d-block mt-1">{errorMessage}</FormFeedback>}
        <InputGroup>
          <InputGroupText>$</InputGroupText>
          <Input
            id={name}
            name={name}
            type="text"
            disabled={disabled}
            value={formattedValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            tabIndex={tabIndex}
            placeholder={placeholder}
            style={style}
            invalid={error}
          />
        </InputGroup>
      </div>
    </FormGroup>
  );
};

export default DollarInput;
